import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  FooterActionBar,
  Icon,
  MobileDrawer,
  Popover,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  CAMPAIGNTYPE_SERVICEACCOUNT,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';
import { CERT_TILE_TYPE } from '../../CertsDetailsList/Tile/constant';
import CertsModalCommentBox from '../../CertsModalCommentBox';
import {
  ACTION_BUTTON_KINDS,
  PREDEFINED_COMMENTS_FOR_ACTION,
} from '../constant';
import { getCommentSubmitLabel } from '../helpers/getCommentSubmitLabel';

import { invertedMessages } from './messages';

import './CertsActionPopover.css';

function CertsActionPopover({
  actionKind,
  isMobileXlargeWidth,
  isCommentPopoverOpen,
  setIsCommentPopoverOpen,
  placement,
  trigger,
  onSubmit,
  // eslint-disable-next-line react/prop-types
  getTypeActEntRole,
}) {
  const [isCritical, setIsCritical] = useState(false);
  const [commentValue, setCommentValue] = useState('');
  const closeButtonRef = useRef(null);
  const intl = useIntl();
  const {
    core: { campaignType },
  } = useSelector((state) => state?.certifications?.details);

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const isSACampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT;
  const { messages } = useSelector((state) => state?.user);

  const l = useInvertedLocalize(invertedMessages);

  const { predefinedComments } = useSelector(
    (state) => state?.certifications?.details?.core
  );

  const APPROVE_TITLE_TEXT =
    messages.CERTS_APPROVE || localize(intl, getMessageKey('CERTS_APPROVE'));

  const CONFIRM_MANAGER_TITLE_TEXT =
    messages.CERTS_CONFIRM_MANAGER ||
    localize(intl, getMessageKey('CERTS_CONFIRM_MANAGER'));

  const CANCEL_BUTTON_TEXT =
    messages.CERTS_CANCEL || localize(intl, getMessageKey('CERTS_CANCEL'));

  // Reset on close
  useEffect(() => {
    if (!isCommentPopoverOpen) {
      setIsCritical(false);
      setCommentValue('');
    }
  }, [isCommentPopoverOpen]);

  useEffect(() => {
    if (isCritical) {
      setIsCritical(false);
    }
  }, [commentValue]);

  const isCommentNotValid = Boolean(
    isCritical || commentValue?.replace(/[<>]/g, '')?.trim()?.length < 3
  );
  const isWorksForMe = actionKind === ACTION_BUTTON_KINDS.WORKS_FOR_ME;

  const handleClose = () => setIsCommentPopoverOpen(false);

  const handleCommentSubmit = () => {
    if (isCommentNotValid) return setIsCritical(true);

    handleClose();

    return onSubmit(commentValue);
  };

  const titleString = `${
    isWorksForMe && !isEOCampaign
      ? CONFIRM_MANAGER_TITLE_TEXT
      : APPROVE_TITLE_TEXT
  } ${l(getTypeActEntRole)}?`;

  const getMobileHeader = () => (
    <Box className='CertsActionPopover-headerWrapper'>
      <Box className='CertsActionPopover-title'>
        <Icon kind='HelpFilled' className='CertsActionPopover-headerIcon' />
        <Typography kind='label' desktop='h3-d' mobile='h1-m' lineLimit={2}>
          {titleString}
        </Typography>
      </Box>
      <Box className='CertsActionPopover-headerButtonIcon'>
        <ButtonIcon
          ref={closeButtonRef}
          size={!isMobileXlargeWidth ? 'small' : 'medium'}
          kind='ghost'
          tabIndex={-1}
          icon={<Icon kind='Close' />}
          onClick={handleClose}
        />
      </Box>
    </Box>
  );

  const getKeyMatch = () => {
    // SA campaign Belong to me for account
    if (
      isSACampaign &&
      actionKind === ACTION_BUTTON_KINDS.APPROVE &&
      getTypeActEntRole === CERT_TILE_TYPE.ACCOUNT
    ) {
      return PREDEFINED_COMMENTS_FOR_ACTION.BELONGS_TO_ME;
    }

    if (
      isEOCampaign &&
      actionKind === ACTION_BUTTON_KINDS.APPROVE &&
      getTypeActEntRole === CERT_TILE_TYPE.ENTITLEMENT
    ) {
      return PREDEFINED_COMMENTS_FOR_ACTION.BELONGS_TO_ME;
    }

    if (actionKind === ACTION_BUTTON_KINDS.APPROVE)
      return PREDEFINED_COMMENTS_FOR_ACTION.APPROVE;
    if (actionKind === ACTION_BUTTON_KINDS.REJECT)
      return PREDEFINED_COMMENTS_FOR_ACTION.REJECT;
    if (actionKind === ACTION_BUTTON_KINDS.WORKS_FOR_ME && !isEOCampaign)
      return PREDEFINED_COMMENTS_FOR_ACTION.WORKS_FOR_ME;
    if (actionKind === ACTION_BUTTON_KINDS.WORKS_FOR_ME && isEOCampaign)
      return PREDEFINED_COMMENTS_FOR_ACTION.BELONGS_TO_ME;
    if (actionKind === ACTION_BUTTON_KINDS.DECOMMISSION && isEOCampaign)
      return PREDEFINED_COMMENTS_FOR_ACTION.DECOMMISSION;

    return null;
  };

  const getMobileContent = () => (
    <CertsModalCommentBox
      defaultComments={predefinedComments[getKeyMatch()]}
      showAlert={isCritical}
      onChange={setCommentValue}
      label={
        messages['comments.ATTESTATIONS'] ||
        localize(intl, getMessageKey('comments.ATTESTATIONS'))
      }
      className='CertsActionPopover-commentBox'
      size='medium'
      allowEditPredefinedComments={
        predefinedComments?.allowEditPredefinedComments
      }
    />
  );

  const getMobileFooter = () => (
    <FooterActionBar size='large' className='CertsActionPopover-controls'>
      <Button type='button' kind='ghost' onClick={handleClose}>
        {CANCEL_BUTTON_TEXT}
      </Button>
      <Button
        type='button'
        kind={!isCommentNotValid ? 'filled' : 'outlined'}
        onClick={handleCommentSubmit}
        className='CertsActionPopover-submtBtn'>
        {getCommentSubmitLabel(actionKind, messages, intl, isEOCampaign)}
      </Button>
    </FooterActionBar>
  );

  // Render one time use multiple places
  const mobileHeader = getMobileHeader();
  const mobileContent = getMobileContent();
  const mobileFooter = getMobileFooter();

  const getPopoverContent = () => (
    <Box className='CertsActionPopover'>
      {mobileHeader}
      {mobileContent}
      <Box tag='div' className='CertsActionPopover-controls'>
        <Button type='button' kind='ghost' onClick={handleClose}>
          {CANCEL_BUTTON_TEXT}
        </Button>
        <Button
          type='button'
          kind={!isCommentNotValid ? 'filled' : 'outlined'}
          onClick={handleCommentSubmit}>
          {getCommentSubmitLabel(actionKind, messages, intl, isEOCampaign)}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box tag='div' className='CertsActionPopover-contextWrapper'>
      {isMobileXlargeWidth ? (
        <>
          {trigger}
          <MobileDrawer
            isOpen={isCommentPopoverOpen}
            setIsOpen={setIsCommentPopoverOpen}
            closeButtonRef={closeButtonRef}
            headerContent={mobileHeader}
            content={mobileContent}
            footerActionBar={mobileFooter}
            className='CertsActionPopover'
          />
        </>
      ) : (
        <Popover
          isPortalEnabled
          placement={placement}
          trigger={trigger}
          popoverContent={getPopoverContent()}
          isOpen={isCommentPopoverOpen}
          setIsOpen={setIsCommentPopoverOpen}
        />
      )}
    </Box>
  );
}

CertsActionPopover.propTypes = {
  actionKind: PropTypes.oneOf(Object.values(ACTION_BUTTON_KINDS)),
  isMobileXlargeWidth: PropTypes.bool.isRequired,
  isCommentPopoverOpen: PropTypes.bool.isRequired,
  setIsCommentPopoverOpen: PropTypes.func.isRequired,
  placement: PropTypes.string.isRequired,
  trigger: PropTypes.element.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

CertsActionPopover.defaultProps = { actionKind: null };

export default CertsActionPopover;
